'use client';

import { useCallback } from 'react';
import { TransactionSignature, PublicKey } from '@solana/web3.js';
import { VaultClient } from '@velocity-exchange/vaults-sdk';
import { notify } from 'src/utils/notifications';
import { useGetTxParams } from '../transactions/useGetTxParams';
import { useWaitForTxConfirm } from '../transactions/useWaitForTxConfirm';
import { mapTxParamsToVaultTxParams } from './utils';

export const useCancelVaultWithdrawal = () => {
	const getTxParams = useGetTxParams();
	const { waitForTxConfirm } = useWaitForTxConfirm();

	const cancelVaultWithdrawalRequest = useCallback(
		async (
			vaultDepositorPubKey: PublicKey,
			vaultClient: VaultClient
		): Promise<TransactionSignature | undefined> => {
			if (!vaultClient || !vaultDepositorPubKey) {
				console.error('No vault client or vault depositor pubkey provided');
				return undefined;
			}

			const toastId = 'cancelVaultWithdrawalRequest';

			try {
				notify({
					type: 'awaiting',
					message: 'Awaiting transaction confirmation',
					id: toastId,
					showUntilCancelled: true,
				});

				const txSig = await vaultClient.cancelRequestWithdraw(
					vaultDepositorPubKey,
					mapTxParamsToVaultTxParams(getTxParams())
				);

				await waitForTxConfirm(txSig);

				notify({
					type: 'success',
					message: 'Withdrawal request canceled.',
					id: toastId,
					updatePrevious: true,
					showUntilCancelled: false,
				});

				return txSig;
			} catch (err: any) {
				console.error(err);
				notify({
					type: 'error',
					message:
						'There was an error canceling a withdrawal request from this vault.',
					id: toastId,
					updatePrevious: true,
					showUntilCancelled: false,
				});
				return undefined;
			}
		},
		[getTxParams, waitForTxConfirm]
	);

	return { cancelVaultWithdrawalRequest };
};
